/**
 * Design System — ROBCO TERMLINK
 * Centralized design tokens for the Pip-Boy terminal portfolio.
 *
 * Two terminal modes ship: Pip-Boy Green (default) and Vault-Tec Amber. They are
 * exported as `darkTheme` / `lightTheme` so every existing `theme.*` consumer keeps
 * working — the toggle now swaps phosphor color, not light/dark.
 */

// ===== COLOR PALETTE (50-100-150 scale) =====
// Every ramp is a terminal phosphor. Names are kept from the previous system so the
// ~18 pages that reference `colors.emerald[500]` etc. retheme without edits.
export const colors = {
  // Terminal chassis — the black the phosphor burns on
  gray: {
    0: '#D6FFE1',      // brightest phosphor "white"
    50: '#A9F5BC',
    100: '#7FE398',
    150: '#5FC177',
    200: '#4A9E5F',
    300: '#3C7E4C',     // subtle text
    400: '#2E5F3A',
    500: '#22452B',     // borders
    600: '#18301E',     // elevated surface
    700: '#0F2115',     // panel surface
    800: '#0A1810',     // card background
    900: '#07120B',     // page background
    950: '#040A06',
    1000: '#000000',
  },

  // Primary — Pip-Boy phosphor green
  primary: {
    50: '#EDFFF1',
    100: '#CDFFD8',
    150: '#9CFFB4',
    200: '#6BFF8C',
    300: '#41FF6B',
    400: '#25F53F',
    500: '#41FF00',    // signature terminal green
    600: '#2FD400',
    700: '#22A300',
    800: '#177000',
    900: '#0C3D00',
  },

  // Alias ramp — used anywhere the old system reached for the brand accent
  skyBlue: {
    50: '#EDFFF1',
    100: '#CDFFD8',
    150: '#9CFFB4',
    200: '#6BFF8C',
    300: '#41FF6B',
    400: '#25F53F',
    500: '#41FF00',
    600: '#2FD400',
    700: '#22A300',
    800: '#177000',
    900: '#0C3D00',
  },

  // Rust — irradiated warning accent
  coral: {
    50: '#FFF0EA',
    100: '#FFD9C9',
    150: '#FFB79B',
    200: '#FF946D',
    300: '#FF7A4D',
    400: '#FF6633',
    500: '#FF5C1F',
    600: '#D94612',
    700: '#A6340B',
    800: '#732307',
    900: '#401203',
  },

  // Bright phosphor — the "healthy" readout green
  emerald: {
    50: '#EBFFF4',
    100: '#C4FFE0',
    150: '#8EFFC4',
    200: '#59FFA8',
    300: '#2EFF8E',
    400: '#18F27C',
    500: '#00E96B',
    600: '#00BF58',
    700: '#009143',
    800: '#00632E',
    900: '#00351A',
  },

  // Vault-Tec amber — the second terminal phosphor
  amber: {
    50: '#FFF6E5',
    100: '#FFE7BC',
    150: '#FFD48A',
    200: '#FFC257',
    300: '#FFB42E',
    400: '#FFB000',
    500: '#FFA000',
    600: '#D98600',
    700: '#A66600',
    800: '#734700',
    900: '#402700',
  },

  // Deep terminal green — recessed data rows
  indigo: {
    50: '#E8FBF2',
    100: '#C2F2DE',
    150: '#8FE3C2',
    200: '#5CD2A5',
    300: '#33BE8B',
    400: '#1FA976',
    500: '#149463',
    600: '#0F7A51',
    700: '#0B5D3E',
    800: '#07402B',
    900: '#042317',
  },

  // Radiation magenta — reserved for rare alert states
  pink: {
    50: '#FFECF3',
    100: '#FFC9DD',
    150: '#FF9CC0',
    200: '#FF6EA3',
    300: '#FF4A8C',
    400: '#F5307A',
    500: '#E31D68',
    600: '#BC1455',
    700: '#8F0F41',
    800: '#62092C',
    900: '#350418',
  },

  // Terminal cyan — diagnostic readouts
  cyan: {
    50: '#E6FDFB',
    100: '#BFF9F3',
    150: '#8CF2E8',
    200: '#59EADC',
    300: '#2EDECE',
    400: '#18CBBB',
    500: '#00B8A9',
    600: '#00968A',
    700: '#00726A',
    800: '#004F49',
    900: '#002B28',
  },

  // Vault jumpsuit yellow-green
  purple: {
    50: '#FBFFE8',
    100: '#F2FFC2',
    150: '#E4FF8F',
    200: '#D4FF5C',
    300: '#C2F533',
    400: '#AEE01F',
    500: '#9BC814',
    600: '#7FA50F',
    700: '#617D0B',
    800: '#425607',
    900: '#232E04',
  },

  // Alert red — failure / offline states
  red: {
    50: '#FFECEA',
    100: '#FFC9C4',
    150: '#FF9B92',
    200: '#FF6D61',
    300: '#FF4C3D',
    400: '#F53829',
    500: '#E62718',
    600: '#BC1E12',
    700: '#8F160D',
    800: '#620E08',
    900: '#350704',
  },

  // Semantic Colors (mapped to scale)
  success: {
    light: '#2EFF8E',  // emerald-300
    base: '#00E96B',   // emerald-500
    dark: '#00BF58',   // emerald-600
  },

  error: {
    light: '#FF4C3D',  // red-300
    base: '#E62718',   // red-500
    dark: '#BC1E12',   // red-600
  },

  warning: {
    light: '#FFB42E',  // amber-300
    base: '#FFA000',   // amber-500
    dark: '#D98600',   // amber-600
  },

  info: {
    light: '#2EDECE',  // cyan-300
    base: '#00B8A9',   // cyan-500
    dark: '#00968A',   // cyan-600
  },
};

// Color arrays for dynamic use. Deliberately narrow: a terminal has a phosphor and a
// couple of alert hues, not a rainbow. Keeping this short is what stops the random
// button colors scattered through the app from breaking the CRT illusion.
export const accentColors = [
  colors.primary,
  colors.emerald,
  colors.amber,
  colors.cyan,
];

export const accentColorPairs = [
  { bg: colors.primary[500], hover: colors.primary[600] },
  { bg: colors.emerald[500], hover: colors.emerald[600] },
  { bg: colors.amber[500], hover: colors.amber[600] },
  { bg: colors.cyan[500], hover: colors.cyan[600] },
];

// ===== TERMINAL MODES =====
// `darkTheme` = Pip-Boy Green (default). `lightTheme` = Vault-Tec Amber.
// Both are dark-screen terminals; the toggle swaps phosphor, not luminance.

export const darkTheme = {
  mode: 'dark',
  phosphorName: 'PIP-BOY GREEN',

  background: '#07120B',
  text: '#B8FFC8',
  primary: colors.primary[500],
  subtleText: '#4E8F5C',
  textSecondary: '#4E8F5C',
  cardBackground: '#0B1A11',
  border: '#1E4A2A',
  logoFilter: 'grayscale(1) brightness(1.6) sepia(1) hue-rotate(60deg) saturate(6)',

  // Contextual variations
  borderLight: 'rgba(65, 255, 0, 0.18)',
  borderMedium: 'rgba(65, 255, 0, 0.32)',
  borderDark: 'rgba(65, 255, 0, 0.5)',
  overlayBg: 'rgba(3, 8, 5, 0.88)',

  // Surface colors
  surface: '#0B1A11',
  surfaceHover: '#102A18',
  surfaceElevated: '#123020',

  // Terminal-specific
  phosphor: colors.primary[500],
  phosphorDim: '#22A300',
  glow: 'rgba(65, 255, 0, 0.55)',
  glowSoft: 'rgba(65, 255, 0, 0.16)',
  scanline: 'rgba(0, 0, 0, 0.35)',
  screenTint: 'rgba(65, 255, 0, 0.05)',
  alert: colors.amber[400],
};

export const lightTheme = {
  mode: 'light',
  phosphorName: 'VAULT-TEC AMBER',

  background: '#100C05',
  text: '#FFD9A0',
  primary: colors.amber[400],
  subtleText: '#9A7132',
  textSecondary: '#9A7132',
  cardBackground: '#171107',
  border: '#4A3616',
  logoFilter: 'grayscale(1) brightness(1.5) sepia(1) hue-rotate(-10deg) saturate(5)',

  // Contextual variations
  borderLight: 'rgba(255, 176, 0, 0.18)',
  borderMedium: 'rgba(255, 176, 0, 0.32)',
  borderDark: 'rgba(255, 176, 0, 0.5)',
  overlayBg: 'rgba(8, 6, 3, 0.88)',

  // Surface colors
  surface: '#1A1208',
  surfaceHover: '#241905',
  surfaceElevated: '#2B1E08',

  // Terminal-specific
  phosphor: colors.amber[400],
  phosphorDim: '#A66600',
  glow: 'rgba(255, 176, 0, 0.55)',
  glowSoft: 'rgba(255, 176, 0, 0.16)',
  scanline: 'rgba(0, 0, 0, 0.35)',
  screenTint: 'rgba(255, 176, 0, 0.05)',
  alert: colors.primary[400],
};

// ===== TYPOGRAPHY =====
// A terminal renders one typeface family: monospace. Display type is the wide,
// squarish Share Tech Mono; body copy is IBM Plex Mono, which stays readable at
// paragraph length where a true bitmap face would not.
export const typography = {
  fontFamily: {
    primary: "'IBM Plex Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Courier New', monospace",
    display: "'Share Tech Mono', 'IBM Plex Mono', Menlo, Monaco, Consolas, monospace",
    boot: "'VT323', 'Share Tech Mono', Menlo, Monaco, monospace",
    code: "'IBM Plex Mono', 'SFMono-Regular', Menlo, Monaco, Consolas, 'Courier New', monospace",
  },

  fontSize: {
    100: '0.625rem',   // 10px
    150: '0.75rem',    // 12px
    200: '0.875rem',   // 14px
    300: '1rem',       // 16px - base
    400: '1.125rem',   // 18px
    500: '1.375rem',   // 22px
    600: '1.625rem',   // 26px
    700: '2rem',       // 32px
    800: '2.5rem',     // 40px
    900: '3.25rem',    // 52px
    1000: '4rem',      // 64px
    1100: '4.75rem',   // 76px
    // Backwards compatibility aliases
    xs: '0.75rem',
    sm: '0.875rem',
    base: '1rem',
    lg: '1.125rem',
    xl: '1.375rem',
    '2xl': '1.625rem',
    '3xl': '2rem',
    '4xl': '2.5rem',
    '5xl': '3.25rem',
  },

  // Monospace faces carry little weight range — the scale is compressed on purpose.
  fontWeight: {
    100: 300,
    200: 300,
    300: 300,
    400: 400,
    500: 400,
    600: 500,
    700: 600,
    800: 700,
    900: 700,
    // Backwards compatibility aliases
    light: 300,
    normal: 400,
    medium: 500,
    semibold: 500,
    bold: 600,
  },

  lineHeight: {
    100: 1,
    200: 1.25,
    300: 1.375,
    400: 1.5,
    500: 1.7,
    600: 2,
    // Backwards compatibility aliases
    tight: 1.2,
    snug: 1.4,
    normal: 1.6,
    relaxed: 1.8,
    loose: 2,
  },

  // Terminal type is tracked out — this is most of the "CRT" read.
  letterSpacing: {
    tight: '0',
    normal: '0.02em',
    wide: '0.08em',
    wider: '0.14em',
    widest: '0.22em',
  },
};

// ===== SPACING (unchanged scale) =====
export const spacing = {
  0: '0',
  50: '0.125rem',
  100: '0.25rem',
  150: '0.375rem',
  200: '0.5rem',
  300: '0.75rem',
  400: '1rem',
  500: '1.25rem',
  600: '1.5rem',
  700: '2rem',
  800: '2.5rem',
  900: '3rem',
  1000: '4rem',
  1100: '5rem',
  1200: '6rem',
  // Backwards compatibility aliases
  xs: '0.25rem',
  sm: '0.5rem',
  md: '0.75rem',
  lg: '1rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '2rem',
  '4xl': '3rem',
  '5xl': '4rem',
  '6xl': '6rem',
};

// ===== SHADOWS =====
// A CRT does not cast drop shadows — it blooms. Every "shadow" is phosphor glow.
export const shadows = {
  100: '0 0 2px rgba(65, 255, 0, 0.18)',
  200: '0 0 4px rgba(65, 255, 0, 0.22)',
  300: '0 0 8px rgba(65, 255, 0, 0.25)',
  400: '0 0 12px rgba(65, 255, 0, 0.28)',
  500: '0 0 18px rgba(65, 255, 0, 0.3)',
  600: '0 0 24px rgba(65, 255, 0, 0.32)',
  700: '0 0 32px rgba(65, 255, 0, 0.35)',
  800: '0 0 40px rgba(65, 255, 0, 0.4)',
  900: '0 0 56px rgba(65, 255, 0, 0.45)',

  inner: 'inset 0 0 24px rgba(0, 0, 0, 0.6)',
  none: 'none',

  // Backwards compatibility aliases
  sm: '0 0 2px rgba(65, 255, 0, 0.18)',
  base: '0 0 8px rgba(65, 255, 0, 0.25)',
  md: '0 0 12px rgba(65, 255, 0, 0.28)',
  lg: '0 0 18px rgba(65, 255, 0, 0.3)',
  xl: '0 0 24px rgba(65, 255, 0, 0.32)',
  buttonHover: '0 0 18px rgba(65, 255, 0, 0.45)',
  buttonActive: '0 0 8px rgba(65, 255, 0, 0.3)',
  darkMd: '0 0 12px rgba(65, 255, 0, 0.28)',
};

// ===== BORDER RADIUS =====
// Terminals draw with box-drawing characters. Nothing is round.
export const borderRadius = {
  0: '0',
  100: '0',
  150: '0',
  200: '0',
  300: '0',
  400: '0',
  500: '0',
  600: '0',
  700: '0',
  full: '0',

  // Backwards compatibility aliases
  none: '0',
  sm: '0',
  base: '0',
  md: '0',
  lg: '0',
  xl: '0',
};

// ===== TRANSITIONS =====
// Terminal redraws snap — easing is deliberately short and linear-ish.
export const transitions = {
  fast: '0.08s steps(3, end)',
  base: '0.14s ease-out',
  slow: '0.3s ease-out',
  all: 'all 0.14s ease-out',
};

// ===== BREAKPOINTS =====
export const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

export const media = {
  sm: `@media (max-width: ${breakpoints.sm})`,
  md: `@media (max-width: ${breakpoints.md})`,
  lg: `@media (max-width: ${breakpoints.lg})`,
  xl: `@media (max-width: ${breakpoints.xl})`,
  '2xl': `@media (max-width: ${breakpoints['2xl']})`,
};

// ===== Z-INDEX SCALE =====
export const zIndex = {
  base: 0,
  dropdown: 10,
  sticky: 20,
  overlay: 30,
  modal: 40,
  popover: 50,
  tooltip: 50,
  crt: 9000,   // scanline/vignette overlay rides above everything
};

// ===== HELPER FUNCTIONS =====

export const getRandomAccentColor = () => {
  return accentColors[Math.floor(Math.random() * accentColors.length)];
};

export const getRandomAccentColorPair = () => {
  return accentColorPairs[Math.floor(Math.random() * accentColorPairs.length)];
};

/**
 * Apply opacity to a hex color
 */
export const hexToRgba = (color, opacity) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
};

/**
 * Phosphor text glow at a given color — the single effect that sells the CRT.
 */
export const textGlow = (color, strength = 1) =>
  `0 0 ${2 * strength}px ${hexToRgba(color, 0.9)}, 0 0 ${8 * strength}px ${hexToRgba(color, 0.45)}`;

export const designSystem = {
  colors,
  accentColors,
  accentColorPairs,
  lightTheme,
  darkTheme,
  typography,
  spacing,
  shadows,
  borderRadius,
  transitions,
  breakpoints,
  media,
  zIndex,
  getRandomAccentColor,
  getRandomAccentColorPair,
  hexToRgba,
  textGlow,
};

export default designSystem;
